import React, { CSSProperties, FC, memo, useState, useEffect } from "react"
import { NewBookingInput, OptionalInputFieldsInput } from "../../../../generated/graphql"
import type { NewBookingInputKeys } from "./bookingInputTypes"
import { Form, Radio, Space } from "antd"
import css from "../StepSummary2.module.css"
import ReactMarkdown from "react-markdown"
import classNames from "classnames"
import { CheckCircleTwoTone, CloseCircleTwoTone, InfoCircleTwoTone } from "@ant-design/icons"
import { green, grey, red } from "@ant-design/colors"

type RadioItem = {
    id?: string
    label?: string
    description?: string
}

type CustomInputRadioGroupProps = {
    field: OptionalInputFieldsInput
    bookingData?: NewBookingInput
    additionalInfoJSON: any
    setAdditionalInfoJSON: (value: any) => void
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    style?: CSSProperties
    handleInputFieldValues?: (values: Array<{ [Key: string]: string | boolean | undefined }>) => void
    showAlert?: boolean
}

const CustomInputRadioGroup: FC<CustomInputRadioGroupProps> = ({ field, bookingData, style, additionalInfoJSON, setAdditionalInfoJSON, updateBookingData, handleInputFieldValues, showAlert }) => {
    const radioItems: RadioItem[] = (field as any).checkboxItems ?? []

    // Helper function to convert a stored label back to an ID
    const labelToId = (storedValue: string): string => {
        const byId = radioItems.find((item) => item.id === storedValue)
        if (byId) return byId.id ?? ""
        const byLabel = radioItems.find((item) => item.label === storedValue)
        return byLabel?.id ?? ""
    }

    const [selectedId, setSelectedId] = useState<string | null>(() => {
        // Initialize from additionalInfoJSON if available
        const stored = additionalInfoJSON[field.label ?? ""]
        if (typeof stored === "string" && stored) {
            return labelToId(stored)
        }
        return null
    })

    const isValid = selectedId !== null
    // F-005: Fehler-Rot erst nach dem ersten Submit-Versuch (showAlert) zeigen.
    const showError = Boolean(showAlert) && !isValid

    useEffect(() => {
        // Sync state when additionalInfoJSON changes externally
        const stored = additionalInfoJSON[field.label ?? ""]
        if (typeof stored === "string" && stored) {
            const restoredId = labelToId(stored)
            setSelectedId(restoredId)
            // Sync validation state so submit button reflects existing selection
            if (restoredId) {
                handleInputFieldValues?.([{
                    [field.name ?? field.label ?? ""]: true
                }])
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalInfoJSON, field.label])

    const handleRadioChange = (itemId: string) => {
        setSelectedId(itemId)

        // Get label for the selected item (for readable storage in additionalInfo)
        const selectedItem = radioItems.find((i: RadioItem) => i.id === itemId)
        const selectedLabel = selectedItem?.label ?? ""

        // Update additionalInfoJSON
        const updatedAdditionalInfoJSON = { ...additionalInfoJSON }
        if (field.label) {
            if (!selectedLabel) {
                delete updatedAdditionalInfoJSON[field.label]
            } else {
                // Store the single label string for readability in admin
                updatedAdditionalInfoJSON[field.label] = selectedLabel
            }
            updateBookingData("additionalInfo", JSON.stringify(updatedAdditionalInfoJSON))
            setAdditionalInfoJSON(updatedAdditionalInfoJSON)

            // Update inputFieldValues for validation
            handleInputFieldValues?.([
                {
                    [field.name ?? field.label]: true,
                },
            ])
        }
    }

    const statusColor = isValid ? green[2] : red[2]
    const statusIcon = (intensity?: number, style?: React.CSSProperties) =>
        isValid ? (
            <CheckCircleTwoTone style={style} twoToneColor={green[intensity ?? 2]} />
        ) : showError ? (
            <CloseCircleTwoTone style={style} twoToneColor={red[intensity ?? 2]} />
        ) : (
            // Neutral vor dem ersten Submit-Versuch (kein Fehler-Rot).
            <CloseCircleTwoTone style={style} twoToneColor={grey[2]} />
        )

    const footerText = "Bitte wähle eine Option"

    return (
        <Form.Item style={style} key={field.name}>
            <div className={css.checkboxGroupContainer} style={{ border: showError ? `1px solid ${red[2]}` : undefined }}>
                {field.label && (
                    <div className={css.checkboxGroupHeadline}>
                        <div className={css.infoIcon}>{statusIcon(3, { fontSize: "1.1em" })}</div>
                        {field.label}
                    </div>
                )}
                {field.description && (
                    <div className={css.checkboxGroupDescription}>
                        <InfoCircleTwoTone className={css.infoIcon} style={{ marginTop: 5 }} />
                        <ReactMarkdown
                            skipHtml={false}
                            className={classNames(css.markdown, css.inputDescription, css.checkboxGroupDescriptionText)}
                            components={{
                                a: ({ node, ...props }) => <a {...props} target='_blank' rel='noopener noreferrer' />,
                            }}
                        >
                            {field.description.replace(/(.*)\n---/g, "$1\n\n---\n\n")}
                        </ReactMarkdown>
                    </div>
                )}
                <Radio.Group value={selectedId} onChange={(e) => handleRadioChange(e.target.value)} style={{ width: "100%" }}>
                    <Space direction='vertical' style={{ width: "100%" }}>
                        {radioItems.map((item: RadioItem, index: number) => (
                            <div key={item.id ?? index} className={classNames(css.checkboxGroupItem, index >= radioItems.length - 1 && css.checkboxGroupItemLast)}>
                                <Radio value={item.id ?? ""}>
                                    <span className={css.checkboxGroupItemLabel}>{item.label}</span>
                                </Radio>
                                {item.description && <div className={css.checkboxGroupItemDescription}>{item.description}</div>}
                            </div>
                        ))}
                    </Space>
                </Radio.Group>
                <div className={classNames(css.checkboxGroupFooter, !showError && css.checkboxGroupFooterHidden)} style={{ backgroundColor: statusColor }}>
                    <div className={css.infoIcon}>{statusIcon()}</div>
                    {footerText}
                </div>
            </div>
        </Form.Item>
    )
}

export default memo(CustomInputRadioGroup)
